import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { DocsService, findDuplicateTemplates, IDbTemplateItem, ITemplateExport, TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION, TemplateLinkExpiredError, templateIdentity } from '@shared/services/docs.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { IAccessMap } from '@shared/services/user.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { isHttpsUrl, isHttpUrl } from '@shared/utils/http-url.utils';
import { catchError, concat, concatMap, defer, EMPTY, from, ignoreElements, map, mergeMap, Observable, of, take, throwError, toArray } from 'rxjs';
import { FailedImport, FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SkippedImport, SuccessFullImport } from './interfaces';

const TEMPLATE_CREATE_PERMISSION = 'docs:TemplateCreate';

/** How many templates the import works on at once - a file of thousands is imported fifty at a time, not all at once. */
const IMPORT_CONCURRENCY = 50;

/**
 * Imports templates from the file the template export writes. Only the import is here - the export is still the templates page's own.
 *
 * Reports like the other transfer services do - as an `ImportResponse` the result popup shows - so a template that fails is listed and the others still go through. What stops the whole import is what makes a template impossible to match: a file that cannot be read, and a template that two in the file, or two existing
 * ones, are - a template is matched on its name, description and tags together.
 */
@Injectable({
	providedIn: 'root',
})
export class TemplateTransferService implements Pick<ITransferService<ITemplateExport>, 'importData'> {
	private readonly translationPrefix = 'docs.templates';

	constructor(
		private docsService: DocsService,
		private sessionStorageService: SessionStorageService,
		private userPermissionsService: UserPermissionsService,
		private translator: TranslateService,
	) {}

	importData(files: File | File[], overwrite: boolean = false): Observable<ImportResponse> {
		// Every template ends in creating one, whether it comes from a key in this environment or a link to another: without the permission nothing is read, fetched or deleted.
		if (!this.userPermissionsService.hasPermissions([TEMPLATE_CREATE_PERMISSION])) {
			return of({
				successfulImports: [],
				errors: [{ status: ImportStatus.ParsingError, message: this.translator.instant(`${this.translationPrefix}.import.errors.missing-permission-create`) } as FailedParse],
			});
		}

		const ImportErrors: ImportErrorTypes[] = [];

		return readFiles(Array.isArray(files) ? [...files] : [files], 'text').pipe(
			concatMap((text) => this.parseTemplates(text, ImportErrors)),
			toArray(),
			mergeMap((templates) => {
				// Nothing read - whatever went wrong is in ImportErrors. Stop here instead of fetching the existing templates only to report an unrelated duplicate over the actual failure.
				if (templates.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateTemplates(templates);

				return this.docsService.getTemplateList().pipe(
					take(1),
					mergeMap(({ result: existingTemplates }) => {
						const rosterDuplicates = findDuplicateTemplates(existingTemplates);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						// Up to fifty templates at a time, each deleting its old one and creating its replacement in turn. A failure is caught for that template alone, so it cuts off none of the others. Every template is its own: none in the file, and none among the existing ones, is the same as another, so no two of them touch the same
						// one. What is left of an import that is interrupted - the page left, say - is up to fifty old templates that were deleted (softly) and not yet replaced, which can be reactivated.
						return from(namesAreAmbiguous ? [] : templates).pipe(
							mergeMap((template) => this.importTemplate(template, existingTemplates, overwrite, ImportErrors), IMPORT_CONCURRENCY),
							toArray(),
							map((successfulImports) => ({ successfulImports, errors: ImportErrors }) as ImportResponse),
						);
					}),
				);
			}),
		);
	}

	private parseTemplates(text: string, ImportErrors: ImportErrorTypes[]): Observable<ITemplateExport> {
		try {
			const escapedTemplates = JSON.parse(text) as ITemplateExport[];
			return from(
				escapedTemplates.map((template) => {
					template.tags = this.unescapeTags(template.tags);
					// A space around the link or key, a common slip when pasting, is taken off here, before anything asks what it is: left on, a url would read as a key to the one check and as a url to another.
					if (typeof template.templateAssetKey === 'string') {
						template.templateAssetKey = template.templateAssetKey.trim();
					}
					return template;
				}),
			);
		} catch {
			ImportErrors.push({
				status: ImportStatus.ParsingError,
				message: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-file`),
			} as FailedParse);
			return EMPTY;
		}
	}

	private importTemplate(template: ITemplateExport, existingTemplates: IDbTemplateItem[], overwrite: boolean, ImportErrors: ImportErrorTypes[]): Observable<SuccessFullImport> {
		const existing = existingTemplates.find((candidate) => templateIdentity(candidate) === templateIdentity(template));

		return defer(() => this.checkAccess(template)).pipe(
			mergeMap(() => {
				if (existing && !overwrite) {
					this.pushSkipped(ImportErrors, template);
					return EMPTY;
				}

				const deleted$ = existing ? this.docsService.deleteTemplate(existing.self).pipe(catchError(() => throwError(() => new Error(this.message('delete-failed', template.name))))) : of(undefined);

				return deleted$.pipe(
					mergeMap(() => this.createImported(template, existing)),
					map(() => ({ Id: template.name, status: existing ? ImportStatus.SuccessUpdate : ImportStatus.SuccessCreate }) as SuccessFullImport),
				);
			}),
			// A failure is this template's alone: it is listed and the next one is still imported.
			catchError((error: Error) => {
				this.pushFailed(ImportErrors, template, error.message);
				return EMPTY;
			}),
		);
	}

	// What the user may import is decided before anything is deleted: an expired link would leave the template deleted with nothing to replace it.
	private checkAccess(template: ITemplateExport): Observable<void> {
		if (isHttpUrl(template.templateAssetKey)) {
			if (!this.hasImportExportPermission()) {
				return throwError(() => new Error(this.message('missing-permission-cross-environment', template.name)));
			}
			// The backend takes a presigned url over https only.
			if (!isHttpsUrl(template.templateAssetKey)) {
				return throwError(() => new Error(this.message('asset-url-not-https', template.name)));
			}
			if (this.hasExpired(template.templateAssetKey)) {
				return throwError(() => new Error(this.message('signed-url-expired', template.name)));
			}
			return of(undefined);
		}

		if (!this.canImportTemplate(template.templateAssetKey)) {
			return throwError(() => new Error(this.message('missing-permission-source-account', template.name)));
		}
		return of(undefined);
	}

	// replaced is the template the overwrite has just deleted, if it deleted one.
	private createImported(imported: ITemplateExport, replaced: IDbTemplateItem | undefined): Observable<unknown> {
		const request = {
			name: imported.name,
			description: imported.description,
			input: imported.input,
			outputs: imported.output,
			tags: this.extractTags(imported.tags),
			assetKey: imported.templateAssetKey,
			version: 1,
		};
		// defer, so a create that throws instead of answering is also an error of the stream - it has to reach the restore below.
		return defer(() => this.docsService.createTemplate(request)).pipe(
			catchError((error) => {
				const expired = error instanceof TemplateLinkExpiredError;
				return replaced ? this.restoreReplaced(replaced, expired) : throwError(() => new Error(this.message(expired ? 'signed-url-expired' : 'create-failed', imported.name)));
			}),
		);
	}

	// The overwrite has already deleted the template (a soft delete) when the create fails - on an expired link, on one that went stale after the local check, on anything the backend refuses. Reactivating it leaves a failed import with what was there, always ending in the error that made it fail.
	private restoreReplaced(replaced: IDbTemplateItem, expired: boolean): Observable<never> {
		const reason = expired ? '-expired' : '';

		return concat(
			this.docsService.reactivateTemplate(replaced.self).pipe(
				ignoreElements(),
				catchError(() => throwError(() => new Error(this.message(`update-failed-existing-lost${reason}`, replaced.name)))),
			),
			throwError(() => new Error(this.message(`update-failed-restored${reason}`, replaced.name))),
		);
	}

	private message(key: string, name: string): string {
		return this.translator.instant(`${this.translationPrefix}.import.errors.${key}`, { name });
	}

	private pushFailed(ImportErrors: ImportErrorTypes[], template: ITemplateExport, message: string): void {
		ImportErrors.push({ Id: template.name, status: ImportStatus.Failed, message } as FailedImport);
	}

	private pushSkipped(ImportErrors: ImportErrorTypes[], template: ITemplateExport): void {
		ImportErrors.push({ Id: template.name, status: ImportStatus.Skipped, message: this.message('exists', template.name) } as SkippedImport);
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, { names: names.join(', ') }),
		} as FailedParse);
	}

	// X-Amz-Date plus X-Amz-Expires is the last moment a presigned url works; a url without both is left to the backend to judge.
	private hasExpired(signedUrl: string): boolean {
		const params = new URL(signedUrl).searchParams;
		const signedAt = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(params.get('X-Amz-Date') ?? '');
		const seconds = Number(params.get('X-Amz-Expires'));
		if (!signedAt || !seconds) {
			return false;
		}
		const [year, month, day, hour, minute, second] = signedAt.slice(1).map(Number);
		return Date.UTC(year, month - 1, day, hour, minute, second) + seconds * 1000 < Date.now();
	}

	// The backend asks for it on the account of the request or at the top of the policy, which is what the permissions of the account the user works in hold.
	private hasImportExportPermission(): boolean {
		return this.userPermissionsService.hasPermissions([TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION]);
	}

	private canImportTemplate(assetKey: string): boolean {
		const parts = assetKey?.split('/');
		if (parts?.[0] !== 'accounts' || !parts[1]) {
			return false;
		}
		const sourceAccountId = parts[1];

		try {
			const accessMap: IAccessMap | null = JSON.parse(this.sessionStorageService.get('User.AccessMap'));
			if (!accessMap) {
				return false;
			}

			// Top check
			if (accessMap.permissions?.includes(TEMPLATE_CREATE_PERMISSION)) {
				return true;
			}

			// Id check and organization-level permission or account-level permission
			return (accessMap.organizations ?? []).some((org) => {
				const account = org?.accounts?.find((a) => a?.id === sourceAccountId);
				if (!account) {
					return false;
				}
				return org?.permissions?.includes(TEMPLATE_CREATE_PERMISSION) || account?.permissions?.includes(TEMPLATE_CREATE_PERMISSION);
			});
		} catch {
			return false;
		}
	}

	extractTags(tags: string): Record<string, string> {
		return Object.fromEntries(tags.split(', ').map((tag) => tag.trim().split('=')));
	}

	unescapeTags(tags: string): string {
		return tags.replace(/;/g, ', ');
	}
}
